import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ModalController } from '@ionic/angular';
import {
  addBlockPeriod,
  addBlockPeriodSuccessfuly,
} from '@lessor/core/store/block-period/block-period.actions';
import {
  addPlayer,
  addPlayerSuccess,
  fetchNewRentInfo,
} from '@lessor/core/store/scheduler/scheduler.actions';
import { SchedulerService } from '@lessor/core/services/scheduler/scheduler.service';
import { PackageService } from '@lessor/core/services/package/package.service';
import {
  selectNewRentInfo,
  selectSchedulerError,
} from '@lessor/core/store/scheduler/scheduler.selectors';
import {
  postEventRent,
  postEventRentSuccess,
} from '@lessor/store/sport-center/sport-center.actions';
import { selectSportCenterLoading } from '@lessor/core/store/sport-center/sport-center.selectors';
import {
  AthletePackageResponse,
  BlockPeriod,
  BookingType,
  BookingWindow,
  Court,
  EventsCourtSchedulerResponse,
  EventsSchedulerResponse,
  NewRentRequest,
  SearchAthleteResponse,
  SignUpPlayerRequest,
  SlotAvailabilityResponse,
  Sport,
  SportCenterRentEquipmentRequest,
} from '@libs/entity-lib';
import { InputMaskLibModule } from '@libs/input-mask';
import { LibPhoneInputComponent, toE164 } from '@libs/phone-input';
import { parseBrDateTime } from '@libs/util-ui';
import { TranslationLibModule } from '@libs/translation';
import {
  canJoinSharedRent,
  CLIENT_PROFILE_OPENER,
  DateUtilService,
  FIND_PLAYER_OPENER,
  getRemainingSpots,
  isSlotBookable,
  unavailableReason,
  vacancyLabel,
} from '@libs/util-services';
import {
  LibAddNewPlayerComponent,
  LibBookingWindowComponent,
  LibWeekSlidesComponent,
} from '@libs/util-ui';
import { Actions, ofType } from '@ngrx/effects';
import { firstValueFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { addIcons } from 'ionicons';
import {
  addOutline,
  alertCircleOutline,
  arrowBackCircleOutline,
  calendarOutline,
  callOutline,
  checkmarkCircle,
  checkmarkCircleOutline,
  chevronDown,
  chevronForward,
  chevronForwardOutline,
  chevronUp,
  closeCircleOutline,
  closeOutline,
  informationCircleOutline,
  lockClosedOutline,
  peopleOutline,
  personAdd,
  personOutline,
  removeOutline,
  searchOutline,
  ticketOutline,
  tennisballOutline,
  timeOutline,
} from 'ionicons/icons';
import {
  AthletePackagesModalComponent,
  SelectedPackageCoupon,
} from './athlete-packages-modal/athlete-packages-modal.component';
import { EquipmentPickerComponent } from './equipment-picker/equipment-picker.component';
import { QuantityStepperComponent } from './quantity-stepper/quantity-stepper.component';

const SHARED_BOOKING_TYPE = 'HORARIO_COMPARTILHADO';

import { IonButton, IonButtons, IonCheckbox, IonContent, IonFooter, IonHeader, IonIcon, IonInput, IonItem, IonSelect, IonSelectOption, IonSpinner, IonTitle, IonToolbar } from '@ionic/angular/standalone';

@Component({
  selector: 'lessor-event-creator-modal',
  templateUrl: './event-creator-modal.component.html',
  styleUrls: ['./event-creator-modal.component.scss'],
  imports: [
    IonButton,
    IonButtons,
    IonCheckbox,
    IonContent,
    IonFooter,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonSelect,
    IonSelectOption,
    IonSpinner,
    IonTitle,
    IonToolbar,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslationLibModule,
    InputMaskLibModule,
    LibPhoneInputComponent,
    LibAddNewPlayerComponent,
    LibBookingWindowComponent,
    LibWeekSlidesComponent,
    EquipmentPickerComponent,
    QuantityStepperComponent
  ],
  providers: [DateUtilService, PackageService],
})
export class EventCreatorModalComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalCtrl = inject(ModalController);
  private readonly destroyRef = inject(DestroyRef);
  private readonly findPlayerOpener = inject(FIND_PLAYER_OPENER, { optional: true });
  private readonly clientProfileOpener = inject(CLIENT_PROFILE_OPENER, { optional: true });
  private readonly schedulerService = inject(SchedulerService);
  private readonly packageService = inject(PackageService);

  // Properties simples — preenchidas pelo ModalController via componentProps (Object.assign).
  // Não usamos input() signals aqui porque o Ionic ModalController atribui valores diretamente,
  // o que substitui a função do signal pelo valor cru e quebra o template.
  @Input() event!: EventsSchedulerResponse;
  @Input() courtId!: number;
  @Input() courtName?: string;
  @Input() courtImage?: string;
  @Input() dateEvent!: string;
  /**
   * Atleta pré-selecionado pela origem que já conhece o cliente
   * (ex: client-360 → "Agendar"). Aplicado no ngOnInit; o operador
   * ainda pode trocar ou cair no walk-in normalmente.
   */
  @Input() initialAthlete?: SearchAthleteResponse;

  // Estado do form
  eventForm: FormGroup = new FormGroup({
    provideCourtId: new FormControl<number | null>(null),
    sportId: new FormControl<number | null>(null),
    eventDate: new FormControl<string>(''),
    idAtleta: new FormControl<number | string>(''),
    nameOwner: new FormControl<string>(''),
    // Recebe E.164 do <lib-phone-input> ('' quando vazio).
    cellphone: new FormControl<string>(''),
    active: new FormControl<boolean>(false),
    maxNumberOfPlayers: new FormControl<number>(1),
    value: new FormControl<string>(''),
    booking: new FormControl<BookingType | null>(null),
    opensAt: new FormControl<number>(0),
    closesAt: new FormControl<number>(0),
  });

  isAdvancedOpen = signal(false);
  isNoWindowBooking = signal(true);

  /**
   * Modo "cliente avulso": usuário digita nome+telefone manualmente porque
   * o cliente não tem cadastro. Backend aceita reserva com nameOwner+cellphone
   * sem idAtleta. Mutuamente exclusivo com atleta selecionado.
   */
  isWalkIn = signal(false);

  /**
   * Atleta encontrado via "Buscar pelo telefone" do walk-in. Alimenta o
   * [athlete] do <lib-add-new-player> para o card selecionado aparecer.
   */
  athleteFromPhoneSearch = signal<SearchAthleteResponse | undefined>(undefined);

  /**
   * Atleta cadastrado ja escolhido. Existe porque `idAtleta` mora no FormGroup,
   * que nao e signal — e a UI precisa reagir a selecao (esconder o card de
   * "cliente sem cadastro", buscar os pacotes dele).
   *
   * `athleteFromPhoneSearch` NAO serve para isso: so e preenchido no caminho da
   * busca por telefone, nao na selecao pelo <lib-add-new-player>.
   */
  selectedAthlete = signal<SearchAthleteResponse | null>(null);

  /** Pacotes que o cliente selecionado tem NESTE centro esportivo. */
  athletePackages = signal<AthletePackageResponse[]>([]);
  loadingAthletePackages = signal(false);

  /**
   * Pacotes que ainda dao cupom: aprovados e com saldo. Pacote cancelado ou
   * zerado nao ajuda quem esta reservando agora.
   *
   * Vencimento NAO sai do `status` — o enum do backend nao tem "expirado";
   * quem responde isso e a `validityDate`.
   */
  usablePackages = computed(() =>
    this.athletePackages().filter(
      (pack) =>
        pack.status === 'APROVADO' &&
        (pack.couponsAvailable ?? 0) > 0 &&
        !this.isPackageExpired(pack),
    ),
  );

  couponsAvailableTotal = computed(() =>
    this.usablePackages().reduce((total, pack) => total + (pack.couponsAvailable ?? 0), 0),
  );

  /** Cupom escolhido no modal de pacotes. Vai como `couponId` no payload. */
  selectedCoupon = signal<SelectedPackageCoupon | null>(null);

  /**
   * O cupom cobre 100% de UMA vaga — a mesma invariante do backend
   * (`PlayerService#addPlayer`: 1 cupom = 1 player). Em horário compartilhado com
   * N vagas, as outras N-1 continuam cobradas.
   */
  couponCoveredValue = computed(() =>
    this.selectedCoupon() ? this._formValueSignal() : 0,
  );

  /** Abre o seletor de pacote/cupom do cliente. */
  async openPackagePicker() {
    const athlete = this.selectedAthlete();
    if (!athlete) return;

    const modal = await this.modalCtrl.create({
      component: AthletePackagesModalComponent,
      componentProps: {
        athleteName: athlete.name,
        packages: this.usablePackages(),
        selectedCouponId: this.selectedCoupon()?.couponId ?? null,
      },
    });
    await modal.present();

    const { data, role } = await modal.onWillDismiss();
    if (role === 'selected' && data) {
      this.selectedCoupon.set(data as SelectedPackageCoupon);
    } else if (role === 'cleared') {
      this.selectedCoupon.set(null);
    }
    this.cdr.markForCheck();
  }

  clearSelectedCoupon() {
    this.selectedCoupon.set(null);
  }

  // Modo de criação. 'rent' = reserva normal (default). 'block' = bloqueio
  // do horário (almoço, manutenção, etc).
  mode = signal<'rent' | 'block'>('rent');

  /**
   * O operador já viu o aviso de ocupação reversível e decidiu seguir.
   *
   * Vai no payload como `overrideOccupied`. Sem isso o backend responde 422
   * SLOT_SOFT_OCCUPIED — que é o certo: cobrança em aberto não é vaga livre, mas
   * também não pode travar a quadra até o PIX morrer.
   */
  readonly overrideOccupied = signal(false);

  /** Motivo do bloqueio (modo='block'). */
  blockReason = signal<string>('');


  /** Sugestões rápidas de motivo. Click preenche `blockReason`. */
  readonly blockReasonSuggestions = [
    'Almoço',
    'Manutenção',
    'Reservado pelo CE',
    'Treino interno',
    'Limpeza',
    'Outro',
  ];

  // Selectors
  newRentInfo = this.store.selectSignal(selectNewRentInfo);
  error = this.store.selectSignal(selectSchedulerError);
  isLoading = this.store.selectSignal(selectSportCenterLoading);

  sports = computed<Sport[]>(() => this.newRentInfo()?.sports ?? []);

  /**
   * `typeProvideCourt` vem do backend (ProvideCourtNewRentResponse). Quando é
   * HORARIO_COMPARTILHADO, o slot aceita múltiplos atletas — o CE precisa
   * declarar quantas vagas está alocando para o cliente atual.
   */
  isSharedSlot = computed(
    () => this.newRentInfo()?.typeProvideCourt === SHARED_BOOKING_TYPE,
  );

  /**
   * Vagas que esta nova reserva pode pegar.
   *
   * `amountAvailableVacancies` vem do backend já descontando reservas existentes
   * no slot — usado quando presente. Quando o slot ainda não tem rent
   * (cenário comum no criar-novo), o backend retorna `null` aqui; nesse caso
   * caímos em `maxNumberOfPlayers` (capacidade total do slot). Se mesmo isso
   * vier nulo, fallback final é 1 pra UI não travar.
   */
  maxVacancies = computed(() => {
    if (!this.isSharedSlot()) return 1;
    const shared = this.newRentInfo()?.sharedInfo;
    return shared?.amountAvailableVacancies ?? shared?.maxNumberOfPlayers ?? 1;
  });

  /**
   * Capacidade total do slot — usada para exibir contexto ("2 de 4 vagas").
   */
  totalVacancies = computed(() => {
    if (!this.isSharedSlot()) return 1;
    return this.newRentInfo()?.sharedInfo?.maxNumberOfPlayers ?? 1;
  });

  /** Vagas alocadas a este cliente nesta reserva. Default 1. */
  vacanciesForThisRent = signal<number>(1);

  /** Equipamentos selecionados no picker (id + quantidade). Vai no payload. */
  selectedEquipments = signal<SportCenterRentEquipmentRequest[]>([]);

  /** Total monetário dos equipamentos — preenchido pelo picker via totalChange. */
  equipmentsTotalValue = signal<number>(0);

  /** Reflete o `value` do form (BRL) em number — fonte para os computeds de total. */
  private readonly _formValueSignal = signal<number>(0);

  /**
   * Total da quadra para esta reserva = valor unitário × vagas alocadas.
   * Aparece no footer pra deixar claro que vagas multiplicam o preço.
   */
  totalCourtValue = computed(() => this._formValueSignal() * this.vacanciesForThisRent());

  /** Total geral da reserva = quadra + equipamentos. Mostrado no botão Confirmar. */
  totalReservation = computed(() =>
    Math.max(
      0,
      this.totalCourtValue() - this.couponCoveredValue() + this.equipmentsTotalValue(),
    ),
  );

  /** Slot lotado (0 vagas livres) — desabilita confirmar quando compartilhado. */
  isSlotFull = computed(() => this.isSharedSlot() && this.maxVacancies() <= 0);

  contextLabel = computed(() => {
    const date = this._dateEventSignal();
    if (!date) return '';
    try {
      const parsed = date.includes('T') ? parseISO(date) : parseISO(`${date}T00:00:00`);
      const formatted = format(parsed, "EEE, d 'de' MMM", { locale: ptBR });
      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    } catch {
      return date;
    }
  });


  // Espelha dateEvent num signal para o computed reagir.
  private readonly _dateEventSignal = signal<string>('');

  // ─── Contexto do slot (quadra / data / horário) ───────────────────────
  //
  // Os @Input acima só SEMEIAM estes signals no ngOnInit. Daqui pra frente a
  // fonte da verdade são os signals — é o que permite trocar de slot sem
  // fechar o modal. Mantemos @Input cru (e não input() signal) pelo motivo
  // documentado acima: o ModalController atribui por Object.assign.
  private readonly _eventSignal = signal<EventsSchedulerResponse | null>(null);
  private readonly _courtIdSignal = signal<number | null>(null);
  private readonly _courtNameSignal = signal<string | undefined>(undefined);
  private readonly _courtImageSignal = signal<string | undefined>(undefined);

  readonly currentEvent = this._eventSignal.asReadonly();
  readonly currentCourtId = this._courtIdSignal.asReadonly();
  readonly currentCourtName = this._courtNameSignal.asReadonly();
  readonly currentCourtImage = this._courtImageSignal.asReadonly();
  readonly currentDate = this._dateEventSignal.asReadonly();

  /** Há slot escolhido? Sem ele o form não tem o que perguntar ao backend. */
  readonly hasSlot = computed(
    () => !!this._eventSignal()?.idProvideCourt && !!this._dateEventSignal(),
  );

  /** Painel de troca de slot aberto. Começa aberto quando abrimos sem slot. */
  readonly isEditingSlot = signal(false);

  /** Quadras + eventos do dia corrente. Alimenta os seletores do painel. */
  readonly dayCourts = signal<EventsCourtSchedulerResponse[]>([]);
  readonly loadingDay = signal(false);

  /**
   * TODOS os horários da quadra do dia, ordenados por hora — os indisponíveis
   * inclusive, para aparecerem riscados.
   *
   * Sumir da lista era pior: o operador não distinguia "esse horário não existe"
   * de "esse horário já está vendido", e ficava procurando o que não ia achar.
   */
  readonly slotOptions = computed<EventsSchedulerResponse[]>(() => {
    const courtId = this._courtIdSignal();
    if (courtId == null) return [];
    const court = this.dayCourts().find((c) => c.courtId === courtId);
    return [...(court?.events ?? [])].sort((a, b) =>
      (a.start ?? '').localeCompare(b.start ?? '')
    );
  });

  /**
   * Disponibilidade REAL do horário escolhido — a única fonte que enxerga holds
   * (atletas em checkout). Buscada na ESCOLHA, não ao pintar a grade: a agenda é
   * cacheada 5 min no backend e o hold vive 10.
   */
  readonly slotAvailability = signal<SlotAvailabilityResponse | null>(null);

  /**
   * Alguém está com este horário na mão agora — hold ou cobrança em aberto.
   *
   * Não bloqueia: hold é camada advisory (ver migration v231) e o CE é dono da quadra.
   * O que muda é que ele passa a SABER antes de confirmar.
   */
  readonly slotContestedBy = computed(() => {
    const a = this.slotAvailability();
    if (!a) return null;
    const held = a.held ?? 0;
    const awaiting = a.awaitingPayment ?? 0;
    if (held === 0 && awaiting === 0) return null;
    return { held, awaiting, seconds: a.secondsUntilAvailable ?? null };
  });

  /** Quantos horários da quadra aceitam este cliente (por qualquer caminho). */
  readonly freeSlotCount = computed(
    () => this.slotOptions().filter((e) => isSlotBookable(e)).length
  );

  /**
   * O slot escolhido já tem reserva e o cliente vai entrar como JOGADOR.
   *
   * Muda o destino do submit: `POST /players` em vez de `POST /rents`. A
   * reserva já existe, então esporte, valor, vagas e janela pertencem a ela —
   * o formulário some e sobra só o cliente.
   */
  readonly isJoiningSharedRent = computed(() => {
    const slot = this._eventSignal();
    return !!slot && canJoinSharedRent(slot);
  });

  /**
   * Vagas restantes no compartilhado escolhido.
   *
   * Prefere a disponibilidade REAL quando ela já chegou: a grade só sabe contar
   * jogadores confirmados, então anunciava "3 vagas livres" num horário cujas três
   * vagas estavam seguradas por atletas no checkout — a tela se contradizia com o
   * próprio aviso logo acima.
   */
  readonly joinRemainingSpots = computed(() => {
    const real = this.slotAvailability()?.remaining;
    if (real !== null && real !== undefined) return real;
    const slot = this._eventSignal();
    return slot ? getRemainingSpots(slot) : 0;
  });

  constructor() {
    addIcons({
      addOutline,
      alertCircleOutline,
      arrowBackCircleOutline,
      calendarOutline,
      callOutline,
      checkmarkCircle,
  checkmarkCircleOutline,
      chevronDown,
  chevronForward,
      chevronForwardOutline,
      chevronUp,
      closeCircleOutline,
  closeOutline,
      informationCircleOutline,
      lockClosedOutline,
      peopleOutline,
      personAdd,
      personOutline,
      removeOutline,
      searchOutline,
      ticketOutline,
      tennisballOutline,
      timeOutline,
    });
  }

  ngOnInit(): void {
    // Semeia os signals de contexto a partir dos @Input. Depois disso, quem
    // manda são os signals (o painel de troca de slot escreve neles).
    //
    // Sem data (aberto pelo Cliente 360) caímos em HOJE: o <lib-week-slides>
    // só monta o swiper quando `dateSelected` chega preenchido — com '' a tira
    // de semana renderiza vazia e não há como escolher dia nenhum.
    this._dateEventSignal.set(this.dateEvent || this.todayYmd());
    this._eventSignal.set(this.event ?? null);
    this._courtIdSignal.set(this.courtId ?? null);
    this._courtNameSignal.set(this.courtName);
    this._courtImageSignal.set(this.courtImage);

    if (this.initialAthlete) {
      // athleteFromPhoneSearch alimenta o [athlete] do <lib-add-new-player>
      // pra o card do selecionado renderizar; setAthleteSelected preenche o
      // form. Os dois são necessários — um só deixa UI e payload divergentes.
      this.athleteFromPhoneSearch.set(this.initialAthlete);
      this.setAthleteSelected(this.initialAthlete);
    }

    // As assinaturas abaixo ficam SEMPRE ativas. Antes elas vinham depois de
    // um early-return por slot ausente, o que deixava o modo "abri sem slot"
    // com um formulário morto: escolher o horário no painel não repintava nada.
    this.store
      .select(selectNewRentInfo)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((info) => {
        if (!info) return;
        this.eventForm.patchValue({
          provideCourtId: this._eventSignal()?.idProvideCourt ?? null,
          sportId: info.sharedInfo?.sport?.id ?? null,
          eventDate: this._dateEventSignal(),
          value: info.value?.toString() ?? '',
          booking: info.typeProvideCourt ?? null,
          opensAt: info.opensAt ?? 0,
          closesAt: info.closesAt ?? 0,
        });
        const sports = info.sports ?? [];
        if (sports.length === 1) {
          this.eventForm.controls['sportId'].setValue(sports[0].id);
        }
        this.cdr?.detectChanges();
      });

    // Espelha o controle `value` num signal para o totalReservation reagir.
    // A diretiva `maskBrlCurrencyFormat` injeta "R$ 120,00"; parse pra number.
    const valueCtrl = this.eventForm.get('value');
    if (valueCtrl) {
      this._formValueSignal.set(this.parseBrl(valueCtrl.value));
      valueCtrl.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((v) => this._formValueSignal.set(this.parseBrl(v)));
    }

    // Fecha modal quando reserva for criada
    this.actions$
      .pipe(ofType(postEventRentSuccess), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.modalCtrl.dismiss({ created: true }));

    // Fecha modal quando bloqueio for criado.
    this.actions$
      .pipe(ofType(addBlockPeriodSuccessfuly), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.modalCtrl.dismiss({ created: true }));

    // Fecha quando o cliente entra num compartilhado existente (POST /players).
    this.actions$
      .pipe(ofType(addPlayerSuccess), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.modalCtrl.dismiss({ created: true }));

    if (this.hasSlot()) {
      // Aberto a partir da agenda: já sabemos o slot.
      this.requestSlotInfo();
    } else {
      // Aberto sem slot (ex: Cliente 360 -> "Agendar"): abre no painel de
      // escolha. O form acorda quando o slot for escolhido.
      this.isEditingSlot.set(true);
      void this.loadDay(this._dateEventSignal());
    }
  }

  // ─── Troca de slot (quadra / data / horário) ──────────────────────────

  /**
   * Pede ao backend os dados do slot corrente (esporte, valor, tipo, janela).
   * A assinatura de `selectNewRentInfo` no ngOnInit repinta o form sozinha —
   * por isso trocar de slot não precisa de nenhuma lógica de formulário.
   */
  private requestSlotInfo() {
    const provideCourtId = this._eventSignal()?.idProvideCourt;
    const date = this._dateEventSignal();
    if (!provideCourtId || !date) return;

    this.store.dispatch(
      fetchNewRentInfo({
        request: { date, idRent: provideCourtId } as NewRentRequest,
      })
    );

    void this.loadSlotAvailability(provideCourtId, date);
  }

  /**
   * Consulta a disponibilidade real do slot. Falha em silêncio de propósito: é um aviso,
   * não uma trava — se a chamada cair, o operador segue reservando como antes.
   */
  private async loadSlotAvailability(provideCourtId: number, date: string) {
    this.slotAvailability.set(null);
    try {
      const availability = await firstValueFrom(
        this.schedulerService.fetchSlotAvailability(provideCourtId, date)
      );
      // Só aplica se o operador ainda estiver no MESMO slot: trocar de horário
      // rápido dispararia duas buscas e a antiga poderia chegar por último.
      if (this._eventSignal()?.idProvideCourt === provideCourtId) {
        this.slotAvailability.set(availability);
      }
    } catch {
      this.slotAvailability.set(null);
    }
  }

  private todayYmd(): string {
    return format(new Date(), 'yyyy-MM-dd');
  }

  /**
   * Regra de disponibilidade — importada de @libs/util-services, NÃO
   * reimplementada aqui.
   *
   * A cópia anterior (herdada do CalendarSlotPickerComponent) conferia
   * `event.idRent`, campo que não existe no contrato: o id mora em
   * `event.rent.idRent`. Todo horário passava como livre e o backend recusava
   * no submit com `400 "Horario já está alugado"`.
   */
  readonly canPick = isSlotBookable;
  readonly pickBlockedReason = unavailableReason;
  readonly slotVacancy = vacancyLabel;

  /** mm:ss — contador de recepção, não de erro. Espelha o formatCountdown do atleta. */
  countdown(seconds: number): string {
    const safe = Math.max(0, Math.floor(seconds));
    return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`;
  }

  /** Quantos horários daquela quadra aceitam o cliente — badge do card. */
  bookableCountFor(court: EventsCourtSchedulerResponse): number {
    return (court.events ?? []).filter((e) => isSlotBookable(e)).length;
  }

  /**
   * Carrega quadras + eventos de um dia. Chamamos o service DIRETO em vez de
   * despachar `fetchScheduler`: aquela action escreve em `scheduler.schedulerDay`,
   * que é lido pela /scheduler legacy e pelo view-rent — navegar de dia aqui
   * dentro contaminaria essas telas.
   */
  private async loadDay(date: string) {
    this.loadingDay.set(true);
    try {
      const day = await firstValueFrom(this.schedulerService.fetch(date));
      const courts = day?.courtEvents ?? [];
      this.dayCourts.set(courts);

      // Preserva a quadra escolhida se ela existir no novo dia — trocar de dia
      // não deve zerar a seleção (é a fricção do slot-picker atual).
      const currentCourtId = this._courtIdSignal();
      const stillThere = courts.find((c) => c.courtId === currentCourtId);
      if (stillThere) {
        this._courtNameSignal.set(stillThere.courtName);
        this._courtImageSignal.set(stillThere.image);
      } else {
        // Nenhuma quadra escolhida (ou a anterior não existe neste dia):
        // assume a PRIMEIRA. Deixar em branco obrigava um toque só para ver
        // que horários existem — e a maioria dos CEs tem uma quadra só.
        const fallback = courts[0] ?? null;
        this._courtIdSignal.set(fallback?.courtId ?? null);
        this._courtNameSignal.set(fallback?.courtName);
        this._courtImageSignal.set(fallback?.image);
      }
    } catch {
      this.dayCourts.set([]);
    } finally {
      this.loadingDay.set(false);
    }
  }

  /** Abre/fecha o painel. Ao abrir pela primeira vez, carrega o dia corrente. */
  toggleSlotEditor() {
    const next = !this.isEditingSlot();
    this.isEditingSlot.set(next);
    if (next && this.dayCourts().length === 0) {
      void this.loadDay(this._dateEventSignal());
    }
  }

  /** <lib-week-slides> devolve "yyyy-MM-dd". Recarrega o dia e solta o horário. */
  onDateChange(date: string) {
    if (!date || date === this._dateEventSignal()) return;
    this._dateEventSignal.set(date);
    this._eventSignal.set(null);
    this.slotAvailability.set(null);
    void this.loadDay(date);
  }

  onCourtChange(courtId: number | null | undefined) {
    if (courtId == null) return;
    const court = this.dayCourts().find((c) => c.courtId === courtId);
    this._courtIdSignal.set(courtId);
    this._courtNameSignal.set(court?.courtName);
    this._courtImageSignal.set(court?.image);
    // Horário antigo pertencia a outra quadra.
    this._eventSignal.set(null);
    this.slotAvailability.set(null);
  }

  /** Escolheu o horário: fecha o painel e pede os dados do slot. */
  /** "Reservar assim mesmo": arma o override e envia. */
  confirmOverridingContested() {
    this.overrideOccupied.set(true);
    this.confirm();
  }

  onSlotChange(event: EventsSchedulerResponse) {
    // Guarda além do [disabled] do template: um clique programático (ou um
    // slot que virou indisponível entre o fetch e o toque) não pode passar.
    if (!event?.idProvideCourt || !isSlotBookable(event)) return;
    this._eventSignal.set(event);
    this.isEditingSlot.set(false);
    // Slot novo, decisão nova: o override não pode vazar de um horário para outro.
    this.overrideOccupied.set(false);
    this.requestSlotInfo();
  }

  setMode(mode: 'rent' | 'block') {
    this.mode.set(mode);
  }

  pickBlockReason(reason: string) {
    this.blockReason.set(reason);
  }

  toggleAdvanced() {
    this.isAdvancedOpen.update((v) => !v);
  }

  setAthleteSelected(athlete: SearchAthleteResponse) {
    if (!athlete) return;
    // Selecionou atleta cadastrado → sai do modo walk-in e limpa o telefone.
    this.isWalkIn.set(false);
    this.selectedAthlete.set(athlete);
    this.eventForm.get('idAtleta')?.setValue(athlete.idAtleta);
    this.eventForm.get('nameOwner')?.setValue(athlete.name);
    this.eventForm.get('cellphone')?.setValue('');
    this.loadAthletePackages(athlete.idAtleta);
  }

  /**
   * Busca os pacotes do cliente no CE. Falha em silencio de proposito: e um
   * auxilio de contexto, nao pode bloquear nem sujar de erro uma reserva que
   * funciona sem ele.
   */
  private loadAthletePackages(athleteId?: number) {
    this.athletePackages.set([]);
    // Cupom pertence ao cliente anterior — trocar de cliente sem limpar mandaria
    // o backend recusar com "Este cupom pertence a outro cliente".
    this.selectedCoupon.set(null);
    if (!athleteId) return;

    this.loadingAthletePackages.set(true);
    this.packageService
      .fetchAthletePackages(athleteId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (packages) => {
          this.athletePackages.set(packages ?? []);
          this.loadingAthletePackages.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.athletePackages.set([]);
          this.loadingAthletePackages.set(false);
        },
      });
  }

  /** `validityDate` chega `dd/MM/yyyy HH:mm` — `new Date()` nao le esse formato. */
  private isPackageExpired(pack: AthletePackageResponse): boolean {
    const validity = parseBrDateTime(pack.validityDate);
    return !!validity && validity.getTime() < Date.now();
  }

  packageValidityLabel(pack: AthletePackageResponse): string | null {
    const validity = parseBrDateTime(pack.validityDate);
    if (!validity) return null;
    return `vence ${new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(validity)}`;
  }

  /**
   * Só oferecemos o atalho quando ha quem abra o perfil. O token e `optional`:
   * sem provider, o card viraria um botao que nao faz nada ao ser tocado.
   */
  canOpenAthleteProfile(): boolean {
    return !!this.clientProfileOpener;
  }

  /** Abre o Cliente 360 do atleta na aba de pacotes, onde da pra agir sobre os cupons. */
  async openAthletePackages() {
    const athleteId = this.selectedAthlete()?.idAtleta;
    if (!athleteId || !this.clientProfileOpener) return;
    await this.clientProfileOpener.open(athleteId, { defaultSegment: 'packages' });
  }

  /**
   * Alterna o modo "cliente avulso". Ao ATIVAR, descarta qualquer atleta
   * previamente selecionado para que o payload não envie idAtleta junto
   * de nameOwner/cellphone. Ao DESATIVAR, limpa os inputs manuais.
   */
  toggleWalkIn() {
    const next = !this.isWalkIn();
    this.isWalkIn.set(next);
    this.eventForm.get('idAtleta')?.setValue(null);
    this.eventForm.get('nameOwner')?.setValue('');
    this.eventForm.get('cellphone')?.setValue('');
    this.eventForm.get('cellphone')?.markAsUntouched();
    // Sem limpar, o organism re-renderizado receberia o atleta antigo via
    // [athlete] e re-selecionaria sozinho — brigando com o toggle.
    this.athleteFromPhoneSearch.set(undefined);
    this.selectedAthlete.set(null);
    this.athletePackages.set([]);
    this.selectedCoupon.set(null);
  }

  /**
   * "Já tem cadastro?" dentro do walk-in: abre a modal Encontrar Jogador na
   * aba Telefone, com o número já digitado — se válido, a busca dispara
   * sozinha. Selecionou → sai do walk-in com o atleta preenchido.
   */
  async openFindByPhone() {
    if (!this.findPlayerOpener) return;
    const result = await this.findPlayerOpener.open({
      initialSegment: 'phone',
      initialPhone: this.eventForm.get('cellphone')?.value ?? '',
    });
    if (result.kind === 'selected') {
      this.athleteFromPhoneSearch.set(result.athlete);
      this.setAthleteSelected(result.athlete);
    }
  }

  /**
   * Erro inline do telefone. O `<lib-phone-input>` entrega E.164 quando
   * válido e o valor cru quando parcial — `toE164` distingue os dois.
   */
  isWalkInPhoneInvalid(): boolean {
    const control = this.eventForm.get('cellphone');
    if (!control?.touched) return false;
    return !toE164(control.value);
  }

  setBookingWindow(window: BookingWindow) {
    this.eventForm.get('opensAt')?.setValue(window.opensAt);
    this.eventForm.get('closesAt')?.setValue(window.closesAt);
  }

  setVacancies(next: number): void {
    const clamped = Math.max(1, Math.min(next, this.maxVacancies()));
    this.vacanciesForThisRent.set(clamped);
  }

  onEquipmentsChange(selection: SportCenterRentEquipmentRequest[]): void {
    this.selectedEquipments.set(selection);
  }

  onEquipmentsTotalChange(total: number): void {
    this.equipmentsTotalValue.set(total);
  }

  /**
   * Converte o valor formatado em BRL ("R$ 120,00", "120,00" ou número) para
   * `number`. Tolerante a undefined/null para não quebrar o pipe.
   */
  private parseBrl(raw: unknown): number {
    if (raw === null || raw === undefined) return 0;
    if (typeof raw === 'number') return raw;
    const str = String(raw).replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
    const n = parseFloat(str);
    return Number.isFinite(n) ? n : 0;
  }

  changeSport(event: Event) {
    const customEvent = event as CustomEvent<{ value: number }>;
    const sportId = customEvent.detail?.value;
    if (sportId) {
      this.eventForm.get('sportId')?.setValue(sportId);
    }
  }

  confirm() {
    // Sem slot escolhido não há o que enviar — o payload precisa de
    // provideCourtId (SportCenterRentRequest) ou do range (BlockPeriod).
    if (!this.hasSlot()) {
      this.isEditingSlot.set(true);
      return;
    }
    if (this.mode() === 'block') {
      this.confirmBlock();
      return;
    }
    if (this.isJoiningSharedRent()) {
      this.confirmJoinSharedRent();
      return;
    }
    this.confirmRent();
  }

  private confirmRent() {
    const value = this.eventForm.getRawValue();

    if (!value.sportId) {
      this.eventForm.get('sportId')?.markAsTouched();
      return;
    }
    // Backend exige: idAtleta OU (nameOwner + cellphone).
    // Ver SportCenterRentController#missInformationOwner.
    if (!value.idAtleta) {
      const name = (value.nameOwner ?? '').trim();
      const phoneE164 = toE164(value.cellphone);
      if (!name || !phoneE164) {
        this.eventForm.get('nameOwner')?.markAsTouched();
        this.eventForm.get('cellphone')?.markAsTouched();
        return;
      }
      // Grava E.164 em tb_jogador.celular. O backend repassa esse valor cru pra
      // Meta API (WhatsAppRentalNotificationHelper), que exige E.164 — enviar
      // mascarado é o que faz a notificação de WhatsApp falhar hoje.
      value.nameOwner = name;
      value.cellphone = phoneE164;
    }

    if (this.isNoWindowBooking()) {
      value.opensAt = null;
      value.closesAt = null;
    }

    // HORARIO_COMPARTILHADO: backend cria N players (1 por vaga) todos vinculados
    // ao mesmo dono. Slots privados sempre enviam 1.
    value.maxNumberOfPlayers = this.isSharedSlot() ? this.vacanciesForThisRent() : 1;

    const equipments = this.selectedEquipments();
    if (equipments.length > 0) {
      value.equipments = equipments;
    }

    // Só viaja quando o operador confirmou por cima do aviso — o default do backend
    // é recusar, e é assim que deve ficar para quem não viu a tela.
    if (this.overrideOccupied()) {
      value.overrideOccupied = true;
    }

    // `value` sai do form como a string mascarada da diretiva ("R$ 120,00"). O
    // backend agora tem o campo tipado como BigDecimal — mandar a máscara é 400.
    // `_formValueSignal` já é o número parseado que alimenta os totais da tela.
    const unitValue = this._formValueSignal();
    value.value = unitValue > 0 ? unitValue : undefined;

    // O backend consome o cupom na MESMA transação da reserva
    // (SportCenterRentCouponService). Se recusar, a reserva inteira volta atrás.
    const coupon = this.selectedCoupon();
    if (coupon) {
      value.couponId = coupon.couponId;
    }

    this.store.dispatch(postEventRent({ request: value }));
  }

  /**
   * Entra num compartilhado que já tem reserva: o cliente vira JOGADOR.
   *
   * `POST /rents` recusaria com "Horario já está alugado" — o backend não
   * aceita segunda reserva no mesmo slot. O endpoint de jogador não pede
   * esporte nem valor: herda tudo da reserva existente.
   */
  private confirmJoinSharedRent() {
    const rentId = this._eventSignal()?.rent?.idRent;
    if (!rentId) return;

    const value = this.eventForm.getRawValue();
    const name = (value.nameOwner ?? '').trim();
    const phoneE164 = toE164(value.cellphone);

    // Mesma invariante do POST /rents: atleta cadastrado OU nome + telefone.
    if (!value.idAtleta && (!name || !phoneE164)) {
      this.eventForm.get('nameOwner')?.markAsTouched();
      this.eventForm.get('cellphone')?.markAsTouched();
      return;
    }

    this.store.dispatch(
      addPlayer({
        playerRequest: {
          rentId,
          athleteId: value.idAtleta,
          name: value.idAtleta ? (value.nameOwner ?? '') : name,
          cellphone: value.idAtleta ? '' : phoneE164,
        } as SignUpPlayerRequest,
      })
    );
  }

  /**
   * Bloqueio do slot atual. Monta um BlockPeriod cobrindo apenas o intervalo
   * `event.start..event.end` no `dateEvent`, restrito a esta quadra. O backend
   * marca os slots dentro desse range como `blocked: true` automaticamente —
   * eles aparecem como cards cinza na grade depois do refresh.
   */
  private confirmBlock() {
    const reason = (this.blockReason() ?? '').trim();
    if (!reason) {
      // marca touched para mostrar erro inline
      return;
    }

    const date = this._dateEventSignal();
    const slot = this._eventSignal();
    const startDate = this.composeDateTime(date, slot?.start);
    const endDate = this.composeDateTime(date, slot?.end);
    if (!startDate || !endDate) return;

    const payload = {
      startDate,
      endDate,
      reason,
      // Backend usa apenas o `id` da quadra; demais campos não são lidos.
      courts: [{ id: this._courtIdSignal() } as unknown as Court],
    } as BlockPeriod;

    this.store.dispatch(addBlockPeriod({ blockPeriod: payload }));
  }

  private composeDateTime(date?: string, time?: string): string | null {
    if (!date || !time) return null;
    const day = date.includes('T') ? date.split('T')[0] : date;
    // garante "HH:mm:ss"
    const t = time.length === 5 ? `${time}:00` : time;
    return `${day}T${t}`;
  }

  dismiss() {
    this.modalCtrl.dismiss({ created: false });
  }
}
